import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Mail, Phone, Pencil, FileSignature, CreditCard, MessageCircle, MessagesSquare, Tablet } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useT } from "../lib/i18n";
import { money, CONSENT_LANGS } from "../lib/helpers";
import { StatusBadge, Empty } from "../components/Common";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { NeedsToKnow } from "../components/AppointmentDrawer";
import AppointmentDrawer from "../components/AppointmentDrawer";
import ConsentDialog from "../components/ConsentDialog";
import PaymentDialog from "../components/PaymentDialog";
import { ClientEditor } from "./Clients";
import { ClinicalNotes } from "../components/ClinicalNotes";
import { Photos } from "../components/Photos";
import { MessageThread } from "../components/MessageThread";
import { EmailDialog } from "../components/EmailDialog";
import { waLink } from "../components/ContactButtons";
import { TemplatePicker } from "../components/TemplatePicker";
import { ClientPackages } from "../components/ClientPackages";
import { HealthScreening, ScreeningAlert } from "../components/HealthScreening";
import { missingProfile, formatAddress } from "../components/ProfileFields";

function SignatureViewer({ id, onClose }) {
  const [s, setS] = useState(null);
  useEffect(() => { if (id) api.get(`/consents/signatures/${id}`).then((r) => setS(r.data)); else setS(null); }, [id]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="signature-viewer">
        <DialogHeader><DialogTitle>{s?.content?.title || s?.template_name}</DialogTitle></DialogHeader>
        {s && (
          <div className="space-y-4 text-sm">
            <p className="whitespace-pre-wrap text-muted-foreground">{s.content?.body}</p>
            {s.signature && <img src={s.signature} alt="signature" className="h-32 border rounded-md bg-white" />}
            <p className="text-xs text-muted-foreground">{s.signer_name} · {s.signed_at && new Date(s.signed_at).toLocaleString()} · v{s.template_version} · {CONSENT_LANGS[s.language]} · IP {s.ip}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ClientProfile() {
  const { id } = useParams();
  const { can, locationId, locations, settings } = useApp();
  const { t, lang } = useT();
  const [c, setC] = useState(null);
  const [edit, setEdit] = useState(false);
  const [consent, setConsent] = useState(false);
  const [pay, setPay] = useState(false);
  const [openAppt, setOpenAppt] = useState(null);
  const [viewSig, setViewSig] = useState(null);
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "history");
  const [mail, setMail] = useState(false);

  const load = useCallback(() => api.get(`/clients/${id}`).then((r) => setC(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!c) return <p className="text-muted-foreground">Loading…</p>;
  const payLoc = locations.find((l) => l.id === locationId) || locations[0];
  const today = new Date().toISOString().slice(0, 10);
  const nextAppt = (c?.appointments || []).filter((a) => a.date >= today && ["confirmed", "pending_deposit", "arrived"].includes(a.status))
    .sort((a, b) => `${a.date}${a.start_time}`.localeCompare(`${b.date}${b.start_time}`))[0];
  const tplVars = { first_name: c?.first_name, clinic_name: settings.branding?.clinic_name || "Bloomwell",
    next_appointment: nextAppt ? `${nextAppt.date} at ${nextAppt.start_time}` : "" };

  return (
    <div className="space-y-8">
      <div className="flex flex-col lg:flex-row gap-6 lg:items-start fade-up">
        <div className="flex items-start gap-5 flex-1">
          <div className="h-16 w-16 rounded-full bg-primary text-primary-foreground grid place-items-center font-display text-xl font-bold shrink-0">
            {(c.first_name[0] || "") + (c.last_name?.[0] || "")}
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground" data-testid="client-code">{c.client_code}</p>
            <h1 className="text-3xl sm:text-4xl font-bold" data-testid="client-name">{c.first_name} {c.last_name}</h1>
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
              {c.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{c.phone}</span>}
              {c.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{c.email}</span>}
              <span>{CONSENT_LANGS[c.preferred_language]}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {can("messages.send") && c.phone && <div className="inline-flex items-center gap-1"><a href={waLink(c.phone, "", payLoc?.timezone)} target="_blank" rel="noreferrer" data-testid="client-whatsapp-btn"><Button variant="outline" className="rounded-full border-emerald-600/40 text-emerald-700 hover:bg-emerald-50"><MessageCircle className="h-4 w-4 mr-1.5" />WhatsApp</Button></a>
            <TemplatePicker vars={tplVars} testId="client-whatsapp-templates" label="" size="icon" onPick={(x) => window.open(waLink(c.phone, x.body, payLoc?.timezone), "_blank", "noopener")} /></div>}
          {can("messages.send") && c.email && <Button variant="outline" className="rounded-full" data-testid="client-email-btn" onClick={() => setMail(true)}><Mail className="h-4 w-4 mr-1.5" />Email</Button>}
          {can("messages.send") && <Button variant="outline" className="rounded-full" data-testid="client-message-btn" onClick={() => setTab("messages")}><MessagesSquare className="h-4 w-4 mr-1.5" />Message</Button>}
          {can("clients.write") && <Button variant="outline" className="rounded-full" data-testid="client-edit-btn" onClick={() => setEdit(true)}><Pencil className="h-4 w-4 mr-1.5" />{t("edit")}</Button>}
          {can("consents.sign") && <Button variant="outline" className="rounded-full" data-testid="client-request-consent-btn" onClick={() => setConsent(true)}><FileSignature className="h-4 w-4 mr-1.5" />{t("request_consent")}</Button>}
          {can("clients.write") && <a href={`/checkin/${c.id}`} data-testid="client-checkin-btn"><Button variant="outline" className="rounded-full"><Tablet className="h-4 w-4 mr-1.5" />Check-in on this device</Button></a>}
          {can("payments.write") && payLoc && <Button className="rounded-full" data-testid="client-take-payment-btn" onClick={() => setPay(true)}><CreditCard className="h-4 w-4 mr-1.5" />{t("take_payment")}</Button>}
        </div>
      </div>
      <NeedsToKnow client={c} />
      <ScreeningAlert client={c} />
      {can("clients.write") && missingProfile(c).length > 0 && (
        <button data-testid="client-missing-details" onClick={() => setEdit(true)} className="w-full text-left rounded-[var(--radius)] border border-amber-500/40 bg-amber-500/5 p-3 text-sm text-amber-900">
          {t("missing_details")}: {missingProfile(c).map((k) => t(k === "address" ? "street_address" : k)).join(", ")} · <span className="underline">{t("edit")}</span>
        </button>
      )}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="history" data-testid="tab-history">{t("history")} ({c.appointments.length})</TabsTrigger>
          {can("clinical.write") && <TabsTrigger value="notes" data-testid="tab-notes">Clinical notes</TabsTrigger>}
          {can("clinical.write") && <TabsTrigger value="photos" data-testid="tab-photos">Before & after</TabsTrigger>}
          <TabsTrigger value="health" data-testid="tab-health">{t("health")}</TabsTrigger>
          <TabsTrigger value="consents" data-testid="tab-consents">{t("consents")} ({c.consents.length})</TabsTrigger>
          {can("payments.read") && <TabsTrigger value="payments" data-testid="tab-payments">{t("payments")}</TabsTrigger>}
          <TabsTrigger value="packages" data-testid="tab-packages">Packages</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">{t("messages")}</TabsTrigger>
          {can("shop.read") && <TabsTrigger value="shop" data-testid="tab-shop">Shop ({c.orders?.length || 0})</TabsTrigger>}
          <TabsTrigger value="details" data-testid="tab-details">{t("details")}</TabsTrigger>
        </TabsList>
        <TabsContent value="history" className="mt-4 space-y-2">
          {!c.appointments.length && <Empty text={t("no_data")} />}
          {c.appointments.map((a) => (
            <button key={a.id} onClick={() => setOpenAppt(a.id)} data-testid={`client-appt-${a.id}`}
              className="w-full text-left rounded-[var(--radius)] border bg-card p-4 flex flex-wrap items-center gap-4 hover:bg-muted/40">
              <span className="font-mono text-sm w-36">{a.date} {a.start_time}</span>
              <div className="flex-1 min-w-[160px]"><div className="font-medium">{a.service_name}</div>
                <div className="text-xs text-muted-foreground">{a.professional_name} · {a.location_name}</div>
                {a.clinical_notes && <div className="text-xs mt-1 italic">{a.clinical_notes}</div>}</div>
              <span className="text-sm">{money(a.price, a.currency, lang)}</span>
              <StatusBadge status={a.status} />
            </button>
          ))}
        </TabsContent>
        <TabsContent value="notes" className="mt-4"><ClinicalNotes client={c} /></TabsContent>
        <TabsContent value="photos" className="mt-4"><Photos client={c} /></TabsContent>
        <TabsContent value="health" className="mt-4"><HealthScreening client={c} onChanged={load} /></TabsContent>
        <TabsContent value="consents" className="mt-4 space-y-2">
          {!c.consents.length && <Empty text={t("no_data")} />}
          {c.consents.map((s) => (
            <div key={s.id} className="rounded-[var(--radius)] border bg-card p-4 flex flex-wrap items-center gap-3" data-testid={`client-consent-${s.id}`}>
              <div className="flex-1"><div className="font-medium">{s.template_name}</div>
                <div className="text-xs text-muted-foreground">{CONSENT_LANGS[s.language]} · {new Date(s.signed_at || s.created_at).toLocaleString()}</div></div>
              <span className={`text-xs rounded-full px-2.5 py-1 ${s.status === "signed" ? "bg-emerald-500/15 text-emerald-800" : "bg-amber-500/15 text-amber-800"}`}>{t(s.status)}</span>
              {s.status === "signed" ? <Button size="sm" variant="ghost" data-testid={`view-signature-${s.id}`} onClick={() => setViewSig(s.id)}>{t("view")}</Button>
                : <a className="text-xs text-primary" target="_blank" rel="noreferrer" href={`/consent/${s.token}`} data-testid={`open-consent-${s.id}`}>{t("sign_now")}</a>}
            </div>
          ))}
        </TabsContent>
        <TabsContent value="payments" className="mt-4 space-y-2">
          {!c.payments.length && <Empty text={t("no_data")} />}
          {c.payments.map((p) => (
            <div key={p.id} className="rounded-[var(--radius)] border bg-card p-4 flex flex-wrap gap-4 text-sm">
              <span className="font-mono w-28">{p.date}</span><span className="flex-1">{p.service_name || p.type} · {p.location_name}</span>
              <span className="capitalize text-muted-foreground">{p.method.replace("_", " ")}</span><span className="font-semibold">{money(p.amount, p.currency, lang)}</span>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="packages" className="mt-4"><ClientPackages client={c} /></TabsContent>
        <TabsContent value="messages" className="mt-4 space-y-2">
          {can("messages.send") && <MessageThread url={`/clients/${c.id}/thread`} self="clinic" testId="client-thread" templateVars={tplVars} placeholder={`Message ${c.first_name} (they'll read it in their patient portal and get an email alert)`} />}
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground pt-4">Emails & WhatsApp sent</p>
          {!c.messages.length && <Empty text={t("no_data")} />}
          {c.messages.map((m) => (
            <div key={m.id} className="rounded-[var(--radius)] border bg-card p-4 flex flex-wrap gap-4 text-sm">
              <span className="font-mono text-xs w-40">{new Date(m.created_at).toLocaleString()}</span>
              <span className="capitalize font-medium">{m.channel}</span><span className="flex-1 text-muted-foreground">{m.subject || m.kind}</span><span className="text-xs">{m.status}</span>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="shop" className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3 rounded-[var(--radius)] border bg-card p-4 text-sm" data-testid="client-shop-access">
            <span>Shop access: <b>{c.shop_status || "none"}</b>{c.source === "shop" && " · came from the shop (lead)"}</span>
            {can("orders.write") && c.shop_status !== "approved" && <Button size="sm" className="rounded-full" data-testid="client-shop-approve-btn" onClick={() => api.post(`/clients/${c.id}/shop-access`, { status: "approved" }).then(load)}>Approve shop access</Button>}
            {can("orders.write") && c.shop_status === "approved" && <Button size="sm" variant="ghost" data-testid="client-shop-revoke-btn" onClick={() => api.post(`/clients/${c.id}/shop-access`, { status: "rejected" }).then(load)}>Revoke</Button>}
          </div>
          {!c.orders?.length && <Empty text={t("no_data")} />}
          {c.orders?.map((o) => (
            <div key={o.id} className="rounded-[var(--radius)] border bg-card p-4 flex flex-wrap gap-4 text-sm" data-testid={`client-order-${o.number}`}>
              <span className="font-mono w-24">{o.number}</span><span className="w-24">{o.created_at.slice(0, 10)}</span>
              <span className="flex-1">{o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</span>
              <span className="capitalize text-muted-foreground">{o.status.replace(/_/g, " ")}</span><span className="font-semibold">{money(o.total, o.currency, lang)}</span>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="details" className="mt-4">
          <dl className="grid sm:grid-cols-2 gap-4 rounded-[var(--radius)] border bg-card p-6 text-sm">
            {[["dob", c.dob], ["gender", c.gender && t(`g_${c.gender}`)], ["address", formatAddress(c)], ["source", c.source], ["marketing", c.marketing_consent ? "✓" : "—"], ["Newsletter", c.newsletter_subscribed ? "Subscribed" : "—"], ["notes", c.notes]].map(([k, v]) => (
              <div key={k}><dt className="text-xs text-muted-foreground">{t(k)}</dt><dd>{v || "—"}</dd></div>
            ))}
          </dl>
        </TabsContent>
      </Tabs>
      <ClientEditor open={edit} onOpenChange={setEdit} client={c} onSaved={load} />
      <ConsentDialog open={consent} onOpenChange={setConsent} client={c} onDone={load} />
      {c.email && <EmailDialog client={c} open={mail} onOpenChange={setMail} onSent={load} templateVars={tplVars} />}
      {payLoc && <PaymentDialog open={pay} onOpenChange={setPay} clientId={c.id} locationId={payLoc.id} currency={payLoc.currency} defaultAmount={0} onSaved={load} />}
      <AppointmentDrawer id={openAppt} onClose={() => setOpenAppt(null)} onChanged={load} />
      <SignatureViewer id={viewSig} onClose={() => setViewSig(null)} />
    </div>
  );
}
